/**
 * CommandDashboard — FOM Operations Command View
 *
 * A full at-a-glance dashboard covering every FOM data domain:
 *   - Shift header (date, shift, weather, supervisor, desk officer)
 *   - Metric strip (on duty, posts, radios, keys, recorders, OOS)
 *   - Officer roster with assigned equipment
 *   - Equipment accountability (keys, radios, recorders, flashlights, badges, access badges, sat phones)
 *   - OOS summary
 *   - Notes + secondary tasks
 *
 * Reads entirely from useDesktopStore — no props, no network calls.
 * When real Tier 2/3 data exists (incidents, checkpoints, geolocation)
 * those panels will be wired in here.
 */
import { useDesktopStore } from '../../stores/desktop'
import { getShiftTime } from '../home/shiftUtils'

// ── Shared style tokens ────────────────────────────────────────────────────

const S = {
  card: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '12px 14px',
  } as React.CSSProperties,
  cardTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    marginBottom: 10,
  } as React.CSSProperties,
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 0',
    borderBottom: '1px solid var(--border)',
    fontSize: 12,
    color: 'var(--text-primary)',
  } as React.CSSProperties,
  lastRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 0',
    fontSize: 12,
    color: 'var(--text-primary)',
  } as React.CSSProperties,
  muted: { color: 'var(--text-muted)', fontSize: 11 } as React.CSSProperties,
  label: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  } as React.CSSProperties,
}

function Dot({ color }: { color: string }) {
  return (
    <span style={{
      width: 8, height: 8, borderRadius: '50%',
      background: color, display: 'inline-block', flexShrink: 0,
    }} />
  )
}

function Badge({ text, color, bg }: { text: string; color: string; bg: string }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px',
      borderRadius: 4, color, background: bg, whiteSpace: 'nowrap',
    }}>
      {text}
    </span>
  )
}

function MetricCard({
  value, label, color, sub, warn,
}: {
  value: string | number
  label: string
  color?: string
  sub?: string
  warn?: boolean
}) {
  return (
    <div style={{
      ...S.card,
      textAlign: 'center',
      borderTop: warn ? '3px solid #f87171' : '3px solid transparent',
      padding: '10px 8px',
    }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      {sub && <div style={{ fontSize: 10, color: warn ? '#f87171' : 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function SectionHeader({ title, count }: { title: string; count?: string | number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
      <span style={S.cardTitle}>{title}</span>
      {count !== undefined && (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{count}</span>
      )}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export default function CommandDashboard() {
  const {
    personnel, deskOfficer,
    securityKeys, otherKeys,
    supRadios, offRadios, otherRadios,
    recorders, flashlights,
    badges, oosIds, activatedBadgeIds,
    accessBadges, satPhones,
    secondaryTasks, notes,
    currentDate, shift, weather,
  } = useDesktopStore()

  const shiftTime = getShiftTime(shift)

  // ── Derived: Personnel ─────────────────────────────────────────────────
  const activeOfficers  = personnel.filter(p => p.name)
  const onDuty          = activeOfficers.length
  const supervisors     = activeOfficers.filter(p =>
    ['Dir/AD/Lt', 'Sergeant', 'OIC', 'Lieutenant', 'Director of Security',
     'Assistant Director', 'Director', 'Developer'].includes(p.role)
  )
  const officers        = activeOfficers.filter(p => p.role === 'Officer')
  const postsAssigned   = activeOfficers.filter(p => p.posts.some(post => post.post && !post.isBreak)).length
  const onBreak         = activeOfficers.filter(p => p.posts.some(post => post.isBreak)).length

  // ── Derived: Equipment ─────────────────────────────────────────────────
  const keysOut         = securityKeys.filter(k => k.assignedTo && !k.oos).length
  const keysOOS         = securityKeys.filter(k => k.oos).length
  const otherKeysOut    = otherKeys.filter(k => !k.available && k.assignedTo).length

  const allRadios       = [...supRadios, ...offRadios, ...otherRadios]
  const radiosOut       = allRadios.filter(r => r.assignedTo && !r.oos).length
  const radiosOOS       = allRadios.filter(r => r.oos).length
  const radiosAvail     = allRadios.filter(r => !r.assignedTo && !r.oos).length

  const recsAssigned    = recorders.filter(r => r.assignedTo && !r.oos).length
  const recsOOS         = recorders.filter(r => r.oos).length
  const recsAvail       = recorders.filter(r => !r.assignedTo && !r.oos).length

  const flashOut        = flashlights.filter(f => f.assignedTo && !f.permanent).length
  const flashOOS        = flashlights.filter(f => f.oos).length

  const badgesOOS       = oosIds.length
  const badgesActivated = activatedBadgeIds.length
  const badgesOnDuty    = badges.filter(b => b.status === 'onDuty').length

  const accessBadgesOut = accessBadges.filter(a => !a.available && !a.oos).length
  const satPhonesOut    = satPhones.filter(s => !s.available && !s.oos).length

  // Total OOS
  const totalOOS = keysOOS + radiosOOS + recsOOS + flashOOS + badgesOOS

  // ── Date string ────────────────────────────────────────────────────────
  const dateStr = currentDate
    ? new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      })
    : 'No date set'

  const supNameStr = supervisors.length
    ? supervisors.map(p => {
        const n = p.name.replace(/\s*\(.*?\)\s*/g, '').trim()
        return n.includes(',') ? n.split(',')[0].trim() : n
      }).join(' / ')
    : '—'

  // ── Colors ─────────────────────────────────────────────────────────────
  const C = {
    green:  '#4ade80',
    amber:  '#fbbf24',
    red:    '#f87171',
    blue:   '#60a5fa',
    purple: '#a78bfa',
    pink:   '#f472b6',
    teal:   '#2dd4bf',
    muted:  'var(--text-muted)',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <div style={{
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid var(--border)',
      }}>
        {/* Gradient bar */}
        <div style={{
          background: 'linear-gradient(135deg,#0a1830 0%,#1e3a5f 60%,#c9a84c22 100%)',
          padding: '14px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>
              ⚡ FOM Command Dashboard
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              {dateStr} · {shift} Shift
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#c9a84c' }}>{shiftTime}</div>
            {weather.temp && (
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>
                {weather.temp}{weather.conditions ? ` · ${weather.conditions}` : ''}
              </div>
            )}
          </div>
        </div>

        {/* Info row */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '10px 20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          borderTop: '1px solid var(--border)',
        }}>
          {[
            { label: 'Supervisor', value: supNameStr },
            { label: 'Desk Officer', value: deskOfficer || '—' },
            { label: 'Shift Time', value: shiftTime },
            { label: 'Officers On Duty', value: String(onDuty) },
          ].map(item => (
            <div key={item.label}>
              <div style={{ ...S.label, color: 'var(--text-muted)' }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── METRIC STRIP ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8 }}>
        <MetricCard value={onDuty}       label="On Duty"      color={C.green} />
        <MetricCard value={postsAssigned} label="On Post"      color={C.blue} />
        <MetricCard value={onBreak}       label="On Break"     color={C.amber} />
        <MetricCard value={radiosOut}     label="Radios Out"   color={C.purple} />
        <MetricCard value={keysOut}       label="Keys Out"     color={C.amber} />
        <MetricCard value={recsAssigned}  label="Recorders"    color={C.pink} />
        <MetricCard value={badgesOOS}     label="Badges OOS"   color={C.red}  warn={badgesOOS > 0} />
        <MetricCard value={totalOOS}      label="Total OOS"    color={totalOOS > 0 ? C.red : C.green} warn={totalOOS > 0} />
      </div>

      {/* ── ROW 1: Officer Roster + Equipment Summary ─────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>

        {/* Officer Roster */}
        <div style={S.card}>
          <SectionHeader title="Officer Roster" count={`${onDuty} active`} />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
            gap: 4,
            marginBottom: 6,
          }}>
            {['Officer', 'Post', 'Radio', 'Key', 'Recorder', 'Badge'].map(h => (
              <div key={h} style={{ ...S.label, color: 'var(--text-muted)', paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
                {h}
              </div>
            ))}
          </div>
          {personnel.map((p, i) => {
            if (!p.name) return null
            const currentPost = p.posts.filter(pp => pp.post && !pp.isBreak).map(pp => pp.post).join(', ')
            const isBreak     = p.posts.some(pp => pp.isBreak)
            const isSup       = ['Dir/AD/Lt','Sergeant','OIC','Lieutenant','Director of Security','Assistant Director','Director','Developer'].includes(p.role)
            return (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                gap: 4,
                padding: '5px 0',
                borderBottom: '1px solid var(--border)',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Dot color={isBreak ? C.amber : C.green} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {p.name.replace(/\s*\(.*?\)\s*/g, '').trim().split(',')[0]}
                  </span>
                  {isSup && (
                    <span style={{ fontSize: 9, background: 'rgba(201,168,76,0.2)', color: '#c9a84c', borderRadius: 3, padding: '1px 4px', fontWeight: 700 }}>
                      SUP
                    </span>
                  )}
                </div>
                <div style={S.muted}>{isBreak ? '🔴 Break' : currentPost || '—'}</div>
                <div style={S.muted}>{p.radio || '—'}</div>
                <div style={S.muted}>{p.key || '—'}</div>
                <div style={S.muted}>{p.recorder || '—'}</div>
                <div style={S.muted}>{p.badge || '—'}</div>
              </div>
            )
          })}
          {onDuty === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 16 }}>
              No officers on duty
            </div>
          )}
        </div>

        {/* Equipment Summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Radios */}
          <div style={S.card}>
            <SectionHeader title="Radios" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.purple }}>{radiosOut}</div>
                <div style={S.muted}>Out</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.green }}>{radiosAvail}</div>
                <div style={S.muted}>Available</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: radiosOOS > 0 ? C.red : C.muted }}>{radiosOOS}</div>
                <div style={S.muted}>OOS</div>
              </div>
            </div>
            {/* Assigned radios list */}
            {[...supRadios, ...offRadios].filter(r => r.assignedTo && !r.oos).map(r => (
              <div key={r.id} style={S.row}>
                <Dot color={C.purple} />
                <span style={{ flex: 1 }}>Radio {r.id}</span>
                <span style={S.muted}>{r.assignedTo}</span>
              </div>
            ))}
            {radiosOOS > 0 && (
              <div style={{ marginTop: 6 }}>
                {allRadios.filter(r => r.oos).map(r => (
                  <div key={r.id} style={{ ...S.row, color: C.red }}>
                    <Dot color={C.red} />
                    <span style={{ flex: 1 }}>Radio {r.id}</span>
                    <Badge text="OOS" color="#fff" bg="#7f1d1d" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Keys */}
          <div style={S.card}>
            <SectionHeader title="Security Keys" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.amber }}>{keysOut}</div>
                <div style={S.muted}>Out</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.green }}>{securityKeys.filter(k => !k.assignedTo && !k.oos).length}</div>
                <div style={S.muted}>Available</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: keysOOS > 0 ? C.red : C.muted }}>{keysOOS}</div>
                <div style={S.muted}>OOS</div>
              </div>
            </div>
            {securityKeys.filter(k => k.assignedTo && !k.oos).map(k => (
              <div key={k.number} style={S.row}>
                <Dot color={C.amber} />
                <span style={{ flex: 1 }}>Key {k.number}</span>
                <span style={S.muted}>{k.assignedTo}</span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ── ROW 2: Recorders + Badges + Other Equipment ──────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>

        {/* Recorders */}
        <div style={S.card}>
          <SectionHeader title="Recorders" count={`${recsAssigned} out · ${recsAvail} avail · ${recsOOS} OOS`} />
          {recorders.map(r => {
            const isOut = !!r.assignedTo && !r.oos
            const isOOS = !!r.oos
            return (
              <div key={r.id} style={S.row}>
                <Dot color={isOOS ? C.red : isOut ? C.pink : C.green} />
                <span style={{ flex: 1 }}>Recorder {r.id}</span>
                {isOOS
                  ? <Badge text="OOS" color="#fff" bg="#7f1d1d" />
                  : isOut
                    ? <span style={S.muted}>{r.assignedTo}</span>
                    : <Badge text="Avail" color="#065f46" bg="#d1fae5" />
                }
              </div>
            )
          })}
        </div>

        {/* Officer Badges + Access Badges */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.card}>
            <SectionHeader title="Officer Badges" count={`${badgesOOS} OOS · ${badgesActivated} activated`} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4 }}>
              {badges.map(b => {
                const isOOS  = oosIds.includes(b.id)
                const isAct  = activatedBadgeIds.includes(b.id)
                const inUse  = b.status === 'onDuty'
                const bg     = isOOS ? '#7f1d1d' : inUse ? '#1e3a5f' : isAct ? '#1e3a5f22' : 'var(--bg-deep)'
                const color  = isOOS ? C.red : inUse ? C.blue : isAct ? '#60a5fa55' : 'var(--text-muted)'
                return (
                  <div key={b.id} style={{
                    textAlign: 'center',
                    fontSize: 11, fontWeight: 700,
                    padding: '4px 2px',
                    borderRadius: 4,
                    background: bg,
                    color,
                    border: `1px solid ${isOOS ? '#991b1b' : 'var(--border)'}`,
                  }}>
                    {b.id}
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 8, height: 8, background: '#7f1d1d', borderRadius: 2, display: 'inline-block' }} /> OOS
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 8, height: 8, background: '#1e3a5f', borderRadius: 2, display: 'inline-block' }} /> On duty
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 8, height: 8, background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 2, display: 'inline-block' }} /> Shadow box
              </span>
            </div>
          </div>

          <div style={S.card}>
            <SectionHeader title="Access Badges + Sat Phones" />
            <div style={{ ...S.row }}>
              <Dot color={C.teal} />
              <span style={{ flex: 1 }}>Access badges out</span>
              <span style={{ fontWeight: 700, color: C.teal }}>{accessBadgesOut}</span>
            </div>
            <div style={S.lastRow}>
              <Dot color={C.blue} />
              <span style={{ flex: 1 }}>Satellite phones out</span>
              <span style={{ fontWeight: 700, color: C.blue }}>{satPhonesOut}</span>
            </div>
          </div>
        </div>

        {/* Flashlights + Other Keys out */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.card}>
            <SectionHeader title="Flashlights" />
            {flashlights.map(f => {
              const isOOS = !!f.oos
              const isOut = !!f.assignedTo && !f.permanent
              const isPerm = !!f.permanent
              return (
                <div key={f.id} style={S.row}>
                  <Dot color={isOOS ? C.red : isPerm ? C.amber : isOut ? C.amber : C.green} />
                  <span style={{ flex: 1 }}>Flashlight {f.id}</span>
                  {isOOS
                    ? <Badge text="OOS" color="#fff" bg="#7f1d1d" />
                    : isPerm
                      ? <span style={S.muted}>Vehicle (permanent)</span>
                      : isOut
                        ? <span style={S.muted}>{f.assignedTo}</span>
                        : <Badge text="Avail" color="#065f46" bg="#d1fae5" />
                  }
                </div>
              )
            })}
          </div>

          <div style={S.card}>
            <SectionHeader title="Other Keys Out" count={`${otherKeysOut} of ${otherKeys.length}`} />
            {otherKeys.filter(k => !k.available && k.assignedTo).length === 0
              ? <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>All keys in</div>
              : otherKeys.filter(k => !k.available && k.assignedTo).map(k => (
                <div key={k.number} style={S.row}>
                  <Dot color={C.amber} />
                  <span style={{ flex: 1, fontSize: 11 }}>{k.description}</span>
                  <span style={S.muted}>{k.assignedTo}</span>
                </div>
              ))
            }
          </div>
        </div>

      </div>

      {/* ── ROW 3: Other Radios + Notes + Secondary Tasks ─────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>

        {/* Other Radios (Management / Engineering / SC) */}
        <div style={S.card}>
          <SectionHeader title="Management & Other Radios" count={`${otherRadios.filter(r => r.assignedTo && !r.oos).length} assigned`} />
          {otherRadios.map(r => {
            const isOOS   = !!r.oos
            const isFixed = !!r.assignedTo && !r.available
            const isFree  = !r.assignedTo && !r.oos

            // Duty override for management radios
            const dutyOn  = r.manualDutyOverride === 'on'
            const dutyOff = r.manualDutyOverride === 'off'

            return (
              <div key={r.id} style={S.row}>
                <Dot color={isOOS ? C.red : isFixed ? C.purple : isFree ? C.green : C.muted} />
                <span style={{ flex: 1 }}>Radio {r.id}</span>
                <span style={S.muted}>{r.assignedTo || (isFree ? 'Available' : '—')}</span>
                {dutyOn  && <Badge text="On duty"  color="#065f46" bg="#d1fae5" />}
                {dutyOff && <Badge text="Off duty" color="#92400e" bg="#fef3c7" />}
                {isOOS   && <Badge text="OOS"      color="#fff"    bg="#7f1d1d" />}
              </div>
            )
          })}
        </div>

        {/* Notes */}
        <div style={S.card}>
          <SectionHeader title="Shift Notes" count={`${notes.length}`} />
          {notes.length === 0
            ? <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No notes for this shift</div>
            : notes.map((note, i) => (
              <div key={i} style={{
                ...S.row,
                alignItems: 'flex-start',
                ...(i === notes.length - 1 ? { borderBottom: 'none' } : {}),
              }}>
                <span style={{ color: '#c9a84c', fontWeight: 700, flexShrink: 0 }}>•</span>
                <span style={{ fontSize: 12, lineHeight: 1.5 }}>{note}</span>
              </div>
            ))
          }
        </div>

        {/* Secondary Tasks */}
        <div style={S.card}>
          <SectionHeader title="Secondary Tasks" count={`${secondaryTasks.length}`} />
          {secondaryTasks.length === 0
            ? <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No secondary tasks assigned</div>
            : secondaryTasks.map((t, i) => (
              <div key={i} style={{
                ...S.row,
                alignItems: 'flex-start',
                ...(i === secondaryTasks.length - 1 ? { borderBottom: 'none' } : {}),
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{t.task}</div>
                  {t.detail && <div style={S.muted}>{t.detail}</div>}
                  {t.callSign && <div style={{ ...S.muted, color: '#c9a84c' }}>{t.callSign}</div>}
                </div>
                {t.time && <span style={S.muted}>{t.time}</span>}
              </div>
            ))
          }
        </div>
      </div>

      {/* ── OOS SUMMARY ──────────────────────────────────────────────── */}
      {totalOOS > 0 && (
        <div style={{
          ...S.card,
          borderLeft: '4px solid #f87171',
          background: 'rgba(239,68,68,0.06)',
        }}>
          <SectionHeader title={`⚠ Out of Service Summary — ${totalOOS} item${totalOOS !== 1 ? 's' : ''}`} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {securityKeys.filter(k => k.oos).map(k => (
              <Badge key={`key-${k.number}`} text={`Key ${k.number}`} color="#fff" bg="#7f1d1d" />
            ))}
            {allRadios.filter(r => r.oos).map(r => (
              <Badge key={`radio-${r.id}`} text={`Radio ${r.id}`} color="#fff" bg="#7f1d1d" />
            ))}
            {recorders.filter(r => r.oos).map(r => (
              <Badge key={`rec-${r.id}`} text={`Recorder ${r.id}`} color="#fff" bg="#7f1d1d" />
            ))}
            {flashlights.filter(f => f.oos).map(f => (
              <Badge key={`fl-${f.id}`} text={`Flashlight ${f.id}`} color="#fff" bg="#7f1d1d" />
            ))}
            {oosIds.map(id => (
              <Badge key={`badge-${id}`} text={`Badge ${id}`} color="#fff" bg="#7f1d1d" />
            ))}
          </div>
        </div>
      )}

      {/* ── FUTURE PANELS PLACEHOLDER ─────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 12,
        opacity: 0.4,
        pointerEvents: 'none',
      }}>
        {[
          { icon: '🗺️', label: 'Terrain Map', tier: 'Terrain+NFC' },
          { icon: '📋', label: 'Incident Reports', tier: 'Terrain+NFC' },
          { icon: '📡', label: 'Radio Contact Log', tier: 'Terrain+NFC' },
        ].map(item => (
          <div key={item.label} style={{
            ...S.card,
            textAlign: 'center',
            padding: '20px 14px',
            border: '1px dashed var(--border)',
          }}>
            <div style={{ fontSize: 28 }}>{item.icon}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginTop: 6 }}>
              {item.label}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Coming — {item.tier}</div>
          </div>
        ))}
      </div>

    </div>
  )
}
